// @ts-nocheck
// Cross-device sync for reader writings (the `fields`, `freeText`, `pracLog`
// state in App.tsx) AND audio playback positions per chapter. Writes are
// persisted to the `progress` table — one row per (user_id, chapter) — so
// a reader who types or listens on their phone picks up the same text and
// the same audio position when they open the book on their laptop.
//
// Storage layout in the `progress.fields` JSONB column:
//   - Chapter N's row stores any field whose key starts with `chN_` (e.g.
//     `ch4_t1_r2_c1`). The chapter-1 row also stores `__freeText` and
//     `__pracLog` under those special keys (App.tsx scopes both to ch1).
//   - Chapter N's row also stores `__audioPos` (number, seconds) for the
//     audio-player resume position — Spotify-style cross-device pickup.
//   - Chapter N's row stores `__ts` — a `{ fieldKey: epochMillis }` map of
//     the last time each field in that row was edited. This is what makes
//     LAST-WRITE-WINS possible across devices.
//
// Conflict resolution = LAST-WRITE-WINS (per field, by edit timestamp):
//   - Each keystroke stamps the edited field with `Date.now()` (tracked by
//     diffing state in the auto-save effect, so we don't have to instrument
//     every onChange in App.tsx). Timestamps are cached in localStorage
//     (`ei_field_times`) so they survive a reload.
//   - On load AND whenever the app regains focus/visibility, we fetch the
//     cloud rows and merge field-by-field: the side with the NEWER timestamp
//     wins. Empty never overwrites non-empty (we don't propagate deletions —
//     a blank field never wipes text written elsewhere). Legacy rows with no
//     `__ts` are treated as timestamp 0, so an actual edited value (ts > 0)
//     always beats stale legacy data, and two legacy values fall back to
//     "local wins" (the old conservative behaviour) — no data loss.
//   - Audio position merges by MAX(local, cloud) — neither side loses
//     listening progress.
//
// Why re-fetch on focus: a phone keeps its browser tab / PWA alive in the
// background, so without this a device that first loaded hours ago would
// show a stale snapshot forever ("I edited on my laptop but my phone won't
// update"). Regaining focus now re-pulls and re-merges.
//
// Save policy:
//   - Auto-save 1.5 s after the last edit (Google-Docs style).
//   - Every push is READ-BEFORE-WRITE: we fetch the current cloud row,
//     merge it with local state (same last-write-wins rule), and upsert the
//     merged result — so a field that only exists on the OTHER device is
//     never clobbered by this device's narrower snapshot.
//   - The explicit "שמור התקדמות" button calls `pushNow()` to flush now.
//   - localStorage continues to cache fields (App.tsx `ei_book_ch1`) and
//     edit-times (`ei_field_times`) for offline / pre-auth use.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

type SyncStatus = "idle" | "loading" | "saving" | "saved" | "error";

const CHAPTER_KEY_RE = /^ch(\d+)_/;
const TIMES_KEY = "ei_field_times";

const num = (x: unknown): number =>
  typeof x === "number" && Number.isFinite(x) ? x : 0;

const hasText = (v: unknown): boolean =>
  typeof v === "string" && v.trim() !== "";

// The keys inside a chapter row that carry reader-written text (everything
// except the bookkeeping keys).
const isContentKey = (k: string): boolean => k !== "__ts" && k !== "__audioPos";

// Last-write-wins for a single field. Empty never beats non-empty; among
// two non-empty values the newer timestamp wins; ties fall back to local.
function pickWinner(
  localVal: unknown,
  localTs: number,
  cloudVal: unknown,
  cloudTs: number,
): { value: string | undefined; ts: number } {
  const l = hasText(localVal);
  const c = hasText(cloudVal);
  if (!c) return { value: l ? (localVal as string) : undefined, ts: localTs };
  if (!l) return { value: cloudVal as string, ts: cloudTs };
  if (cloudTs > localTs) return { value: cloudVal as string, ts: cloudTs };
  return { value: localVal as string, ts: localTs };
}

function loadTimes(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(TIMES_KEY) || "{}");
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

function persistTimes(times: Record<string, number>): void {
  try {
    localStorage.setItem(TIMES_KEY, JSON.stringify(times));
  } catch {
    /* quota / private-mode — non-fatal, cloud still has the times */
  }
}

// Split a flat `fields` object into one bucket per chapter. Empty values
// are skipped — we don't want to upsert rows for chapters the reader
// hasn't touched.
function groupFieldsByChapter(fields: Record<string, string>): Map<number, Record<string, any>> {
  const groups = new Map<number, Record<string, any>>();
  for (const [k, v] of Object.entries(fields)) {
    if (!hasText(v)) continue;
    const m = k.match(CHAPTER_KEY_RE);
    const ch = m ? parseInt(m[1], 10) : 1;
    if (!groups.has(ch)) groups.set(ch, {});
    groups.get(ch)![k] = v;
  }
  return groups;
}

// Merge a local chapter-row payload into whatever is currently in the cloud
// for that chapter, field-by-field last-write-wins. Preserves cloud-only
// fields (written by another device) and the max audio position.
function mergeRow(
  cloudRow: Record<string, any> | null | undefined,
  localRow: Record<string, any>,
): Record<string, any> {
  const cloud = cloudRow || {};
  const cloudTs = (cloud.__ts && typeof cloud.__ts === "object" ? cloud.__ts : {}) as Record<string, number>;
  const localTs = (localRow.__ts && typeof localRow.__ts === "object" ? localRow.__ts : {}) as Record<string, number>;
  const out: Record<string, any> = {};
  const outTs: Record<string, number> = {};
  const keys = new Set<string>([
    ...Object.keys(cloud).filter(isContentKey),
    ...Object.keys(localRow).filter(isContentKey),
  ]);
  for (const k of keys) {
    const w = pickWinner(localRow[k], num(localTs[k]), cloud[k], num(cloudTs[k]));
    if (hasText(w.value)) {
      out[k] = w.value;
      if (w.ts > 0) outTs[k] = w.ts;
    }
  }
  if (Object.keys(outTs).length > 0) out.__ts = outTs;
  const a = Math.max(num(cloud.__audioPos), num(localRow.__audioPos));
  if (a > 0) out.__audioPos = a;
  return out;
}

type Params = {
  user: any;
  hasAccess: boolean;
  authLoading: boolean;
  accessLoading: boolean;
  fields: Record<string, string>;
  setFields: (f: Record<string, string>) => void;
  freeText: string;
  setFreeText: (t: string) => void;
  pracLog: string;
  setPracLog: (t: string) => void;
};

export function useCloudProgress(params: Params) {
  const {
    user,
    hasAccess,
    authLoading,
    accessLoading,
    fields,
    setFields,
    freeText,
    setFreeText,
    pracLog,
    setPracLog,
  } = params;

  const [status, setStatus] = useState<SyncStatus>("idle");
  // Per-chapter audio playback position (seconds). Object instead of Map
  // so it's React-state friendly and trivial to use in the auto-save
  // useEffect dep array.
  const [audioPositions, setAudioPositions] = useState<Record<number, number>>({});
  const initialLoadDone = useRef(false);
  const saveTimer = useRef<number | null>(null);
  // Per-field last-edit timestamps (epoch ms). Cached in localStorage so
  // last-write-wins survives a reload.
  const fieldTimesRef = useRef<Record<string, number>>(loadTimes());
  // Snapshot of the last state we've reconciled, so the auto-save effect can
  // diff against it to detect which fields were freshly edited (and stamp
  // only those) — without instrumenting every onChange in App.tsx.
  const prevSnapshotRef = useRef<{ fields: Record<string, string>; freeText: string; pracLog: string }>({
    fields: {},
    freeText: "",
    pracLog: "",
  });
  const prevAudioRef = useRef<Record<number, number> | undefined>(undefined);
  const syncingRef = useRef(false);
  const lastSyncRef = useRef(0);
  // Keep the latest state in a ref so callbacks don't need to be
  // re-memoised on every keystroke.
  const latest = useRef({ fields, freeText, pracLog, user, hasAccess, audioPositions });
  latest.current = { fields, freeText, pracLog, user, hasAccess, audioPositions };

  // Build the per-chapter row payloads (fields + __ts + __audioPos) from the
  // current local state.
  const buildPayloads = useCallback(() => {
    const { fields, freeText, pracLog, audioPositions } = latest.current;
    const times = fieldTimesRef.current;
    const groups = groupFieldsByChapter(fields);
    const ch1 = groups.get(1) ?? {};
    if (hasText(freeText)) ch1.__freeText = freeText;
    if (hasText(pracLog)) ch1.__pracLog = pracLog;
    if (Object.keys(ch1).length > 0) groups.set(1, ch1);
    // Attach a per-chapter __ts map for the content keys present in the row.
    for (const [, rowFields] of groups.entries()) {
      const ts: Record<string, number> = {};
      for (const k of Object.keys(rowFields)) {
        if (!isContentKey(k)) continue;
        const t = num(times[k]);
        if (t > 0) ts[k] = t;
      }
      if (Object.keys(ts).length > 0) rowFields.__ts = ts;
    }
    // Layer the per-chapter audio positions into the same row payloads.
    // We upsert a row for any chapter with a non-zero position, even if
    // the reader hasn't written anything in it yet — listen-only readers
    // still want resume to work.
    for (const [chStr, pos] of Object.entries(audioPositions)) {
      const ch = parseInt(chStr, 10);
      if (!Number.isFinite(ch) || ch < 1) continue;
      if (typeof pos !== "number" || !Number.isFinite(pos) || pos <= 0) continue;
      const existing = groups.get(ch) ?? {};
      existing.__audioPos = pos;
      groups.set(ch, existing);
    }
    return groups;
  }, []);

  // Public setter — AudioPlayer (via App.tsx) calls this whenever a
  // chapter's playback position should be persisted. Throttling lives
  // on the AudioPlayer side; here we just update state and let the
  // auto-save effect debounce.
  const setAudioPosition = useCallback((chapter: number, sec: number) => {
    if (typeof chapter !== "number" || !Number.isFinite(chapter)) return;
    if (typeof sec !== "number" || !Number.isFinite(sec) || sec < 0) return;
    setAudioPositions((prev) => {
      // Skip the state churn if the value rounds to the same value
      // (avoids a re-render storm during play).
      const prevVal = prev[chapter];
      if (typeof prevVal === "number" && Math.abs(prevVal - sec) < 0.5) return prev;
      return { ...prev, [chapter]: sec };
    });
  }, []);

  // Set of chapter numbers we've already bumped via touchChapter() in
  // THIS browser session — a one-bump-per-session throttle keeps casual
  // tab-switches from spamming the table. Page reload clears the set
  // and a fresh row is written, which is the right behavior: "user is
  // back today" is exactly the signal we want.
  const touchedThisSession = useRef<Set<number>>(new Set());

  // Mark a chapter as "viewed" — the reader opened it but may not write
  // or play audio. Without this, the progress table would never log
  // passive readers, and the admin Users table would understate how
  // many chapters someone has actually engaged with. (See AdminUsers.tsx
  // for the "פעילות אחרונה" + "התקדמות" columns that consume this.)
  //
  // We only push the columns that change (user_id, chapter, last_read_at)
  // so the existing `fields` JSONB on an existing row — writings + audio
  // position — is left intact by Postgres' ON CONFLICT DO UPDATE. New
  // rows get the column's `default '{}'::jsonb` (see migration 0001).
  const touchChapter = useCallback(async (chapter: number): Promise<void> => {
    const { user, hasAccess } = latest.current;
    if (!user || !hasAccess) return;
    if (typeof chapter !== "number" || !Number.isFinite(chapter)) return;
    if (chapter < 1 || chapter > 12) return;
    if (touchedThisSession.current.has(chapter)) return;
    // Optimistic — add to the set before awaiting so concurrent calls
    // don't double-fire. On error we remove it so the next attempt can
    // retry within the same session.
    touchedThisSession.current.add(chapter);
    try {
      const { error } = await supabase.from("progress").upsert(
        {
          user_id: user.id,
          chapter,
          last_read_at: new Date().toISOString(),
        },
        { onConflict: "user_id,chapter" },
      );
      if (error) throw error;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("touchChapter failed", e);
      touchedThisSession.current.delete(chapter);
    }
  }, []);

  // Force an immediate save, bypassing the debounce. Called by the
  // explicit "שמור התקדמות" button so the reader gets the familiar
  // instant feedback. Read-before-write per chapter so a field that only
  // exists on another device is never clobbered.
  const pushNow = useCallback(async (): Promise<boolean> => {
    const { user, hasAccess } = latest.current;
    if (!user || !hasAccess) return false;
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setStatus("saving");
    const groups = buildPayloads();
    try {
      for (const [chapter, chFields] of groups.entries()) {
        const { data: existing, error: readErr } = await supabase
          .from("progress")
          .select("fields")
          .eq("user_id", user.id)
          .eq("chapter", chapter)
          .maybeSingle();
        if (readErr) throw readErr;
        const merged = mergeRow(existing?.fields, chFields);
        const { error } = await supabase.from("progress").upsert(
          {
            user_id: user.id,
            chapter,
            fields: merged,
            last_read_at: new Date().toISOString(),
          },
          { onConflict: "user_id,chapter" },
        );
        if (error) throw error;
      }
      setStatus("saved");
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("cloud progress save failed", e);
      setStatus("error");
      return false;
    }
  }, [buildPayloads]);

  // Fetch every progress row for this user and merge cloud → local with the
  // last-write-wins rule. Used for the initial load AND for the re-fetch on
  // focus/visibility. Safe to call repeatedly; guarded against overlap.
  const syncFromCloud = useCallback(async (): Promise<void> => {
    const { user, hasAccess } = latest.current;
    if (!user || !hasAccess) return;
    if (syncingRef.current) return;
    syncingRef.current = true;
    setStatus("loading");
    try {
      const { data, error } = await supabase
        .from("progress")
        .select("chapter, fields")
        .eq("user_id", user.id);
      if (error) {
        // eslint-disable-next-line no-console
        console.error("cloud progress load failed", error);
        setStatus("error");
        return;
      }

      // Flatten the per-chapter cloud rows into flat value + timestamp maps,
      // the two ch1-special strings, and a per-chapter audio position map.
      const cloudFields: Record<string, string> = {};
      const cloudTimes: Record<string, number> = {};
      let cloudFreeText = "";
      let cloudPracLog = "";
      const cloudAudioPositions: Record<number, number> = {};
      for (const row of (data as any[]) || []) {
        const rowFields = (row.fields || {}) as Record<string, unknown>;
        const rowTs = (rowFields.__ts && typeof rowFields.__ts === "object"
          ? rowFields.__ts
          : {}) as Record<string, number>;
        for (const [k, v] of Object.entries(rowFields)) {
          if (k === "__ts") continue;
          if (k === "__audioPos") {
            if (typeof v === "number" && Number.isFinite(v) && v > 0) {
              cloudAudioPositions[row.chapter] = v;
            }
            continue;
          }
          if (!hasText(v)) continue;
          const t = num(rowTs[k]);
          if (k === "__freeText" && row.chapter === 1) {
            cloudFreeText = v as string;
            cloudTimes.__freeText = t;
          } else if (k === "__pracLog" && row.chapter === 1) {
            cloudPracLog = v as string;
            cloudTimes.__pracLog = t;
          } else {
            cloudFields[k] = v as string;
            cloudTimes[k] = t;
          }
        }
      }

      // Merge field-by-field, last-write-wins.
      const localFields = latest.current.fields || {};
      const localTimes = fieldTimesRef.current || {};
      const merged: Record<string, string> = { ...localFields };
      const mergedTimes: Record<string, number> = { ...localTimes };
      const keys = new Set<string>([...Object.keys(localFields), ...Object.keys(cloudFields)]);
      for (const k of keys) {
        const w = pickWinner(localFields[k], num(localTimes[k]), cloudFields[k], num(cloudTimes[k]));
        if (hasText(w.value)) {
          merged[k] = w.value as string;
          if (w.ts > 0) mergedTimes[k] = w.ts;
        }
      }
      const fw = pickWinner(latest.current.freeText, num(localTimes.__freeText), cloudFreeText, num(cloudTimes.__freeText));
      const pw = pickWinner(latest.current.pracLog, num(localTimes.__pracLog), cloudPracLog, num(cloudTimes.__pracLog));
      if (hasText(fw.value) && fw.ts > 0) mergedTimes.__freeText = fw.ts;
      if (hasText(pw.value) && pw.ts > 0) mergedTimes.__pracLog = pw.ts;

      // Commit. Update the reconciliation snapshot + times BEFORE the state
      // setters so the auto-save effect that fires on these setFields calls
      // sees "no fresh local edit" and doesn't re-stamp cloud-adopted values
      // with a local now().
      fieldTimesRef.current = mergedTimes;
      persistTimes(mergedTimes);
      prevSnapshotRef.current = {
        fields: merged,
        freeText: hasText(fw.value) ? (fw.value as string) : "",
        pracLog: hasText(pw.value) ? (pw.value as string) : "",
      };
      setFields(merged);
      if ((fw.value || "") !== (latest.current.freeText || "")) setFreeText((fw.value as string) || "");
      if ((pw.value || "") !== (latest.current.pracLog || "")) setPracLog((pw.value as string) || "");
      // Seed audioPositions from cloud with a MAX merge so no listening
      // progress is lost.
      if (Object.keys(cloudAudioPositions).length > 0) {
        setAudioPositions((prev) => {
          const next = { ...prev };
          for (const [chStr, pos] of Object.entries(cloudAudioPositions)) {
            const ch = parseInt(chStr, 10);
            next[ch] = Math.max(num(next[ch]), num(pos));
          }
          return next;
        });
      }

      lastSyncRef.current = Date.now();
      setStatus("saved");
    } finally {
      syncingRef.current = false;
      initialLoadDone.current = true;
    }
  }, [setFields, setFreeText, setPracLog]);

  // Initial load: once auth + access resolve, pull and merge. Free-preview
  // readers (no access) stay local-only.
  useEffect(() => {
    if (initialLoadDone.current) return;
    if (authLoading || accessLoading) return;
    if (!user) return;
    if (!hasAccess) {
      initialLoadDone.current = true;
      return;
    }
    syncFromCloud();
  }, [user, hasAccess, authLoading, accessLoading, syncFromCloud]);

  // Re-fetch whenever the app regains focus / becomes visible again, so a
  // device that was in the background pulls in edits made elsewhere. Debounced
  // to at most once every 3 s to avoid churn on rapid focus/blur.
  useEffect(() => {
    const maybeSync = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const { user, hasAccess } = latest.current;
      if (!user || !hasAccess) return;
      if (!initialLoadDone.current) return;
      if (Date.now() - lastSyncRef.current < 3000) return;
      syncFromCloud();
    };
    document.addEventListener("visibilitychange", maybeSync);
    window.addEventListener("focus", maybeSync);
    return () => {
      document.removeEventListener("visibilitychange", maybeSync);
      window.removeEventListener("focus", maybeSync);
    };
  }, [syncFromCloud]);

  // Auto-save + edit-timestamp tracking. Runs on every state change once the
  // initial load is done. Diffs against the last reconciled snapshot to stamp
  // freshly-edited fields with now(), then debounces a cloud push.
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (!user || !hasAccess) return;

    const prev = prevSnapshotRef.current;
    const now = Date.now();
    const times = { ...fieldTimesRef.current };
    let changed = false;

    const keys = new Set<string>([...Object.keys(prev.fields || {}), ...Object.keys(fields || {})]);
    for (const k of keys) {
      const a = (prev.fields || {})[k] ?? "";
      const b = (fields || {})[k] ?? "";
      if (a !== b) {
        if (hasText(b)) times[k] = now;
        changed = true;
      }
    }
    if ((prev.freeText || "") !== (freeText || "")) {
      if (hasText(freeText)) times.__freeText = now;
      changed = true;
    }
    if ((prev.pracLog || "") !== (pracLog || "")) {
      if (hasText(pracLog)) times.__pracLog = now;
      changed = true;
    }
    if (changed) {
      fieldTimesRef.current = times;
      persistTimes(times);
      prevSnapshotRef.current = { fields: { ...fields }, freeText, pracLog };
    }

    const audioChanged = audioPositions !== prevAudioRef.current;
    prevAudioRef.current = audioPositions;

    if (!changed && !audioChanged) return;

    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      pushNow();
    }, 1500);
    return () => {
      if (saveTimer.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [fields, freeText, pracLog, audioPositions, user, hasAccess, pushNow]);

  return { status, pushNow, audioPositions, setAudioPosition, touchChapter };
}
